import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, KeyRound, Copy, Check, AlertTriangle } from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

/**
 * MFA settings / security tab. Currently supports:
 *   - Viewing enrollment status + recovery codes remaining
 *   - Regenerating (reissuing) the 10 single-use recovery codes
 *     (Requires a fresh authenticator code; invalidates ALL prior codes.)
 */
export default function MfaSettings() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const status = user?.mfa_status;
  const [showReissue, setShowReissue] = useState(false);
  const [ack, setAck] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (!status?.enrolled) {
      nav("/mfa/setup");
    }
  }, [user, status, nav]);

  const reissue = async () => {
    if (!ack || code.trim().length !== 6) return;
    setBusy(true);
    try {
      const r = await api.post("/mfa/recovery-codes/reissue", { code: code.trim() });
      setNewCodes(r.data.recovery_codes);
      toast.success("Recovery codes regenerated. Prior codes are no longer valid.");
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate codes");
    } finally {
      setBusy(false);
    }
  };

  const downloadCodes = () => {
    if (!newCodes) return;
    const body =
      `ProSe.Claims — MFA Recovery Codes (Regenerated)\nGenerated ${new Date().toISOString()}\n\n` +
      newCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n") +
      "\n\nEach code works once. Store them somewhere safe.\n";
    const blob = new Blob([body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prose-claims-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyCode = (c, idx) => {
    navigator.clipboard.writeText(c);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1200);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[820px] mx-auto px-6 py-12 md:py-16 flex-1 w-full" data-testid="mfa-settings-page">
        <div className="label-tiny mb-3">Security</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-slate-900">
          <ShieldCheck className="inline-block w-9 h-9 mr-2 -mt-2 text-slate-700" strokeWidth={1.75} />
          Two-factor authentication
        </h1>

        <div className="mt-8 border border-slate-200 p-6 md:p-8" data-testid="mfa-status-block">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat label="Enrolled" value={status?.enrolled ? "Yes" : "No"} tone={status?.enrolled ? "ok" : "warn"} />
            <Stat label="This session" value={status?.verified ? "Verified" : "Not verified"} tone={status?.verified ? "ok" : "warn"} />
            <Stat label="Recovery codes left" value={String(status?.recovery_codes_remaining ?? 0)} tone={(status?.recovery_codes_remaining ?? 0) >= 3 ? "ok" : "warn"} />
          </div>
        </div>

        {/* Reissue */}
        <div className="mt-8 border border-slate-200 p-6 md:p-8" data-testid="mfa-reissue-block">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-5 h-5 text-amber-600" strokeWidth={1.75} />
            <h2 className="font-serif-display text-2xl text-slate-900">Regenerate recovery codes</h2>
          </div>
          <p className="text-sm text-slate-700 max-w-2xl">
            Running low, lost the last set, or think one may have leaked? Generate a fresh
            set of 10 codes. This invalidates every prior recovery code — including any
            unused ones.
          </p>

          {!newCodes ? (
            <>
              {!showReissue ? (
                <Button
                  className="mt-5 rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                  onClick={() => setShowReissue(true)}
                  data-testid="show-reissue-btn"
                >
                  Regenerate codes
                </Button>
              ) : (
                <div className="mt-5 space-y-4">
                  <label className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ack}
                      onChange={(e) => setAck(e.target.checked)}
                      className="mt-0.5"
                      data-testid="reissue-ack-checkbox"
                    />
                    <span className="text-sm text-amber-900 leading-relaxed">
                      <AlertTriangle className="inline w-4 h-4 mr-1 -mt-1" />
                      I understand that all of my prior recovery codes (used or unused) will stop working immediately.
                    </span>
                  </label>
                  <div>
                    <Label className="label-tiny mb-2 block">Enter your current 6-digit authenticator code</Label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      maxLength={6}
                      autoComplete="one-time-code"
                      placeholder="123456"
                      className={`${inputCls} font-mono-tech tracking-widest text-lg max-w-[220px]`}
                      data-testid="reissue-code-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={reissue}
                      disabled={!ack || code.length !== 6 || busy}
                      className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="reissue-submit-btn"
                    >
                      {busy ? "Regenerating…" : "Regenerate now"}
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-none border-slate-300"
                      onClick={() => { setShowReissue(false); setAck(false); setCode(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="mt-5" data-testid="new-recovery-codes">
              <div className="border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 mb-3">
                Save these codes now. They will not be shown again.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                {newCodes.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => copyCode(c, i)}
                    className="border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 font-mono-tech text-xs text-slate-800 flex items-center justify-between gap-2 group"
                    data-testid={`new-recovery-code-${i}`}
                    title="Click to copy"
                  >
                    <span>{c}</span>
                    {copiedIdx === i ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100" />}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                className="rounded-none border-slate-300"
                onClick={downloadCodes}
                data-testid="download-new-codes-btn"
              >
                Download .txt
              </Button>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({ label, value, tone = "ok" }) {
  const toneCls =
    tone === "ok"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className={`border p-4 ${toneCls}`}>
      <div className="font-mono-tech text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-serif-display text-2xl mt-1">{value}</div>
    </div>
  );
}
